/**
 * Currency and number formatting utilities for Worthly
 */

// All supported currencies
export const CURRENCIES = [
  { code: 'CAD', name: 'Dollar', subtitle: 'CAD, AUD, USD, NZD', symbol: '$', flag: '\uD83C\uDDE8\uD83C\uDDE6', locale: 'en-CA' },
  { code: 'SGD', name: 'Singapore Dollar', subtitle: 'SGD', symbol: 'S$', flag: '\uD83C\uDDF8\uD83C\uDDEC', locale: 'en-SG' },
  { code: 'EUR', name: 'Euro', subtitle: 'EUR', symbol: '\u20AC', flag: '\uD83C\uDDEA\uD83C\uDDFA', locale: 'en-IE' },
  { code: 'GBP', name: 'British Pound', subtitle: 'GBP', symbol: '\u00A3', flag: '\uD83C\uDDEC\uD83C\uDDE7', locale: 'en-GB' },
  { code: 'NGN', name: 'Nigerian Naira', subtitle: 'NGN', symbol: '\u20A6', flag: '\uD83C\uDDF3\uD83C\uDDEC', locale: 'en-NG' },
  { code: 'INR', name: 'Indian Rupee', subtitle: 'INR', symbol: '\u20B9', flag: '\uD83C\uDDEE\uD83C\uDDF3', locale: 'en-IN' },
  { code: 'JPY', name: 'Japanese Yen', subtitle: 'JPY', symbol: '\u00A5', flag: '\uD83C\uDDEF\uD83C\uDDF5', locale: 'ja-JP' },
  { code: 'ZAR', name: 'South African Rand', subtitle: 'ZAR', symbol: 'R', flag: '\uD83C\uDDFF\uD83C\uDDE6', locale: 'en-ZA' },
  { code: 'BRL', name: 'Brazilian Real', subtitle: 'BRL', symbol: 'R$', flag: '\uD83C\uDDE7\uD83C\uDDF7', locale: 'pt-BR' },
  { code: 'CNY', name: 'Chinese Yuan', subtitle: 'CNY', symbol: '\u00A5', flag: '\uD83C\uDDE8\uD83C\uDDF3', locale: 'zh-CN' },
  { code: 'MXN', name: 'Mexican Peso', subtitle: 'MXN', symbol: '$', flag: '\uD83C\uDDF2\uD83C\uDDFD', locale: 'es-MX' },
  { code: 'KRW', name: 'South Korean Won', subtitle: 'KRW', symbol: '\u20A9', flag: '\uD83C\uDDF0\uD83C\uDDF7', locale: 'ko-KR' },
  { code: 'AED', name: 'UAE Dirham', subtitle: 'AED', symbol: '\u062F.\u0625', flag: '\uD83C\uDDE6\uD83C\uDDEA', locale: 'ar-AE' },
  { code: 'CHF', name: 'Swiss Franc', subtitle: 'CHF', symbol: 'Fr', flag: '\uD83C\uDDE8\uD83C\uDDED', locale: 'de-CH' },
  { code: 'GHS', name: 'Ghanaian Cedi', subtitle: 'GHS', symbol: '\u20B5', flag: '\uD83C\uDDEC\uD83C\uDDED', locale: 'en-GH' },
  { code: 'KES', name: 'Kenyan Shilling', subtitle: 'KES', symbol: 'KSh', flag: '\uD83C\uDDF0\uD83C\uDDEA', locale: 'en-KE' },
  { code: 'PKR', name: 'Pakistani Rupee', subtitle: 'PKR', symbol: '\u20A8', flag: '\uD83C\uDDF5\uD83C\uDDF0', locale: 'ur-PK' },
  { code: 'PHP', name: 'Philippine Peso', subtitle: 'PHP', symbol: '\u20B1', flag: '\uD83C\uDDF5\uD83C\uDDED', locale: 'en-PH' },
  { code: 'IDR', name: 'Indonesian Rupiah', subtitle: 'IDR', symbol: 'Rp', flag: '\uD83C\uDDEE\uD83C\uDDE9', locale: 'id-ID' },
  { code: 'TRY', name: 'Turkish Lira', subtitle: 'TRY', symbol: '\u20BA', flag: '\uD83C\uDDF9\uD83C\uDDF7', locale: 'tr-TR' },
  { code: 'MYR', name: 'Malaysian Ringgit', subtitle: 'MYR', symbol: 'RM', flag: '\uD83C\uDDF2\uD83C\uDDFE', locale: 'ms-MY' },
  { code: 'HKD', name: 'Hong Kong Dollar', subtitle: 'HKD', symbol: 'HK$', flag: '\uD83C\uDDED\uD83C\uDDF0', locale: 'en-HK' },
];

// Get current currency from user data or default
let _currentCurrency = CURRENCIES[0]; // Default: Dollar (CAD)

export const setCurrentCurrency = (code) => {
  const found = CURRENCIES.find(c => c.code === code);
  if (found) _currentCurrency = found;
};

export const getCurrentCurrency = () => _currentCurrency;

/**
 * Format a number as currency with 2 decimal places
 */
export const formatCurrency = (amount) => {
  const c = _currentCurrency;
  // JPY and KRW don't use decimal places
  const noDecimals = ['JPY', 'KRW'].includes(c.code);
  return new Intl.NumberFormat(c.locale, {
    style: 'currency',
    currency: c.code,
    minimumFractionDigits: noDecimals ? 0 : 2,
    maximumFractionDigits: noDecimals ? 0 : 2,
  }).format(amount || 0);
};

/**
 * Format a number as currency with no decimal places
 */
export const formatCurrencyWhole = (amount) => {
  const c = _currentCurrency;
  return new Intl.NumberFormat(c.locale, {
    style: 'currency',
    currency: c.code,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

/**
 * Format a number as short currency (e.g., 15k, 1.2k)
 */
export const formatCurrencyShort = (amount, currencyOverride) => {
  // Tiered short-form currency: 1.2K, 12.5M, 1.3B, 3.4T.
  // Fixes overflow of large values (e.g. NGN millions) inside narrow UI.
  const sym = (_currentCurrency && _currentCurrency.symbol) || '$';
  if (amount == null || isNaN(amount)) return `${sym}0`;

  const negative = amount < 0;
  const abs = Math.abs(amount);

  let value;
  let suffix;
  if (abs >= 1_000_000_000_000) {
    value = abs / 1_000_000_000_000;
    suffix = 'T';
  } else if (abs >= 1_000_000_000) {
    value = abs / 1_000_000_000;
    suffix = 'B';
  } else if (abs >= 1_000_000) {
    value = abs / 1_000_000;
    suffix = 'M';
  } else if (abs >= 1_000) {
    value = abs / 1_000;
    suffix = 'K';
  } else {
    return `${negative ? '-' : ''}${sym}${Math.round(abs)}`;
  }

  const rounded = value.toFixed(1);
  return `${negative ? '-' : ''}${sym}${rounded}${suffix}`;
};

/**
 * w2_04: for paths, where there's little room. The full amount up to 99,999,
 * then shortened: $150k, NGN 1.5M, 2.3B.
 */
export const formatCurrencyCompact = (amount) => {
  const n = Number(amount) || 0;
  const a = Math.abs(n);
  if (a < 100000) return formatCurrencyWhole(n);
  const sym = (_currentCurrency && _currentCurrency.symbol) || '$';
  const sign = n < 0 ? '-' : '';
  const trim = (x) => String(x).replace(/\.0$/, '');
  const k = Math.round(a / 1000);
  if (k < 1000) return sign + sym + k + 'k';
  const m = (a / 1e6).toFixed(1);
  if (Number(m) < 1000) return sign + sym + trim(m) + 'M';
  return sign + sym + trim((a / 1e9).toFixed(1)) + 'B';
};

/**
 * Get possessive form of a name
 */
export const getPossessiveName = (name) => {
  if (!name) return "Your";
  const trimmedName = name.trim();
  if (trimmedName.toLowerCase().endsWith('s')) {
    return `${trimmedName}'`;
  }
  return `${trimmedName}'s`;
};
